"use client";

import { env } from "@klarify/env";
import { Trans } from "@lingui/react/macro";
import { ArrowRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { ContactTherapistModal } from "./contact-therapist-modal";

const BASE_BUTTON_CLASS =
	"group h-auto gap-2 rounded-xl bg-klarify-forest-700 text-white shadow-sm transition-all hover:bg-klarify-forest-800 hover:shadow-md";
const HERO_BUTTON_CLASS =
	"mt-2 w-fit px-6 py-3.5 font-semibold text-base sm:text-lg";

interface Props {
	slug: string;
	firstName: string;
	lastName: string;
	credentialsSuffix: string | null;
	referrerPostId?: string | null;
	/**
	 * Tag the inquiry with a specific therapist when the CTA is rendered on
	 * a therapist sub-page or from a post byline that resolves to one.
	 * Unset on the SOLO clinic profile (inquiry is clinic-level only).
	 */
	therapistId?: string;
	/** Override the button label. Defaults to "Send a message". */
	label?: string;
	/** Override layout/sizing (replaces hero defaults like w-fit / px-6). */
	className?: string;
	/**
	 * When the template renders inside the editor's Preview mode (and the
	 * onboarding step 2 preview), the contact flow shouldn't actually open
	 * the inquiry modal — the therapist is reviewing what visitors will
	 * see, not sending themselves a message. Renders the button visually
	 * the same but no-ops on click.
	 */
	isPreview?: boolean;
	/** Icon rendered before the label. Replaces the trailing arrow. */
	leadingIcon?: ReactNode;
	/** Runs right before the inquiry modal opens. Not called in preview. */
	onOpen?: () => void;
}

export const ContactCta = ({
	slug,
	firstName,
	lastName,
	credentialsSuffix,
	referrerPostId,
	therapistId,
	label,
	className,
	isPreview = false,
	leadingIcon,
	onOpen,
}: Props) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<Button
				type="button"
				variant="unstyled"
				rounding="none"
				onClick={() => {
					if (isPreview) {
						return;
					}
					onOpen?.();
					setOpen(true);
				}}
				aria-disabled={isPreview || undefined}
				className={cn(BASE_BUTTON_CLASS, className ?? HERO_BUTTON_CLASS)}
			>
				{leadingIcon}
				<span>{label ?? <Trans>Send a message</Trans>}</span>
				{leadingIcon != null ? null : (
					<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 sm:h-5 sm:w-5" />
				)}
			</Button>
			{isPreview ? null : (
				<ContactTherapistModal
					open={open}
					onOpenChange={setOpen}
					slug={slug}
					firstName={firstName}
					lastName={lastName}
					credentialsSuffix={credentialsSuffix}
					turnstileSiteKey={env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
					referrerPostId={referrerPostId}
					therapistId={therapistId}
				/>
			)}
		</>
	);
};
